import {
  PbECommerceSettingsPlatformEnum,
  SearchCatalogKindEnum
} from '@itsrever/rever-api'
import {
  NonReturnableListKey,
  nonReturnableListsFor
} from '@/application/manage-return-conditions'
import { useCatalogItems, useScopePortals } from '@/application/queries'
import { CatalogKind } from '@/domain/catalog'
import {
  GroupReturnConditions,
  NonReturnableLists
} from '@/domain/return-conditions'
import { useEcommerceId } from '@/hooks/use-ecommerce-id'
import { useStorePlatform } from '@/hooks/use-store-platform'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { FormProps } from '@/ui/components/group-settings'
import {
  CatalogCopyKey,
  CatalogPicker,
  FreeTextCopyKey,
  FreeTextListField
} from '@/ui/components/settings-components/non-returnable-form'

type ListKey = keyof NonReturnableLists

// The group stores the lists under its own names; the pickers and their copy use the portal's.
const LIST_KEYS: Record<NonReturnableListKey, ListKey> = {
  productIds: 'productIds',
  collectionIds: 'collectionIds',
  productTypes: 'categoryIds',
  productTags: 'tagIds',
  orderTags: 'orderTagIds'
}

const FREE_TEXT_COPY: Record<NonReturnableListKey, FreeTextCopyKey> = {
  productIds: 'product_ids',
  collectionIds: 'collection_ids',
  productTypes: 'category_ids',
  productTags: 'tag_ids',
  orderTags: 'order_tags'
}

const PICKERS: { key: ListKey; kind: CatalogKind; copyKey: CatalogCopyKey }[] =
  [
    {
      key: 'productIds',
      kind: SearchCatalogKindEnum.Product,
      copyKey: 'products'
    },
    {
      key: 'collectionIds',
      kind: SearchCatalogKindEnum.Collection,
      copyKey: 'collections'
    },
    {
      key: 'categoryIds',
      kind: SearchCatalogKindEnum.ProductType,
      copyKey: 'product_types'
    },
    {
      key: 'tagIds',
      kind: SearchCatalogKindEnum.ProductTag,
      copyKey: 'product_tags'
    }
  ]

// A platform the evaluators don't know still gets every list, rather than an empty tab.
const listsFor = (platform?: string) => {
  const lists = nonReturnableListsFor(platform)
  return lists.length
    ? lists
    : (Object.keys(LIST_KEYS) as NonReturnableListKey[])
}

// The portal whose catalog and platform the lists are edited against: in the group view, a Shopify
// portal if any, as only Shopify can search its catalog.
function useCatalogPortal() {
  const { scope } = useWarehousesScope()
  const loadedEcommerceId = useEcommerceId()
  const storePlatform = useStorePlatform()
  const { portals, loadingPortals } = useScopePortals()
  if (scope.kind === 'store')
    return { ecommerceId: scope.ecommerceId, platform: storePlatform }
  const platformOf = (store?: string) =>
    portals.find((portal) => portal.id === store)?.platform
  const stores = scope.kind === 'group' ? scope.stores : []
  const ecommerceId = loadingPortals
    ? undefined
    : (stores.find(
        (store) => platformOf(store) === PbECommerceSettingsPlatformEnum.Shopify
      ) ??
      stores[0] ??
      loadedEcommerceId)
  // The group view loads its first portal's settings, the fallback when the portal list has no platform.
  return { ecommerceId, platform: platformOf(ecommerceId) ?? storePlatform }
}

export const NonReturnableForm: React.FC<FormProps<GroupReturnConditions>> = ({
  value,
  saved,
  readOnly,
  onChange
}) => {
  const { ecommerceId, platform } = useCatalogPortal()
  const lists = value.nonReturnable
  const set = (key: ListKey) => (ids: string[]) =>
    onChange({ ...value, nonReturnable: { ...lists, [key]: ids } })

  if (platform !== PbECommerceSettingsPlatformEnum.Shopify)
    return (
      <div className="flex flex-col gap-4">
        {listsFor(platform).map((key) => (
          <FreeTextListField
            key={key}
            copyKey={FREE_TEXT_COPY[key]}
            platform={platform}
            value={lists[LIST_KEYS[key]]}
            onChange={set(LIST_KEYS[key])}
            canEdit={!readOnly}
          />
        ))}
        {/* The catalog has no brand search, so brands are always typed in. */}
        <FreeTextListField
          copyKey="brand_ids"
          value={lists.brandIds}
          onChange={set('brandIds')}
          canEdit={!readOnly}
        />
      </div>
    )
  return (
    <div className="flex flex-col gap-4">
      {PICKERS.map(({ key, kind, copyKey }) => (
        <ListPicker
          key={key}
          ecommerceId={ecommerceId}
          kind={kind}
          copyKey={copyKey}
          value={lists[key]}
          saved={saved.nonReturnable[key]}
          readOnly={readOnly}
          onChange={set(key)}
        />
      ))}
      {/* The catalog has no brand search, so brands are always typed in. */}
      <FreeTextListField
        copyKey="brand_ids"
        value={lists.brandIds}
        onChange={set('brandIds')}
        canEdit={!readOnly}
      />
      <FreeTextListField
        copyKey="order_tags"
        value={lists.orderTagIds}
        onChange={set('orderTagIds')}
        canEdit={!readOnly}
      />
    </div>
  )
}

function ListPicker({
  ecommerceId,
  kind,
  copyKey,
  value,
  saved,
  readOnly,
  onChange
}: {
  ecommerceId?: string
  kind: CatalogKind
  copyKey: CatalogCopyKey
  value: string[]
  saved: string[]
  readOnly: boolean
  onChange: (ids: string[]) => void
}) {
  const resolved = useCatalogItems(ecommerceId, kind, saved)
  const known = new Map(resolved.items.map((item) => [item.id, item]))
  // Only saved ids can be stale: anything picked here came from the store.
  const staleIds = resolved.resolved
    ? value.filter((id) => saved.includes(id) && !known.has(id))
    : []
  return (
    <CatalogPicker
      ecommerceId={ecommerceId}
      kind={kind}
      copyKey={copyKey}
      value={value}
      known={known}
      staleIds={staleIds}
      onChange={onChange}
      canEdit={!readOnly}
      loading={resolved.loading}
    />
  )
}
